import { useState } from 'react'

import { useThresholdConfiguration } from '../hooks/useThresholdConfiguration'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  ColorScheme,
  Grid,
  Modal,
  Text,
  TextInput,
} from '@hivewatch/dark'

import { MAX_THRESHOLD, MIN_THRESHOLD } from '@/utils/forms/validation'
import { tVar } from '@/utils/i18n'

import { DarkEditDeviceSection } from '../../dark/DarkEditDeviceSection'
import { DarkEditDeviceSkeleton } from '../../dark/DarkEditDeviceSkeleton'

interface DarkThresholdConfigurationSectionIProps {
  colorScheme: ColorScheme
  deviceId: string
  onSaveCallback?: () => void
}

export const DarkThresholdConfigurationSection = ({
  colorScheme,
  deviceId,
  onSaveCallback,
}: DarkThresholdConfigurationSectionIProps) => {
  const { t } = useTranslation()
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false)
  const {
    accessGrantedThreshold,
    doorCloseThreshold,
    doorForcedBounceThreshold,
    handleAccessGrantedChange,
    handleDoorCloseChange,
    handleDoorForcedBounceChange,
    isDefault,
    isDeleteLoading,
    isLoading,
    isResetDisabled,
    isSaveDisabled,
    isUpsertLoading,
    onResetToDefaults,
    onSave,
  } = useThresholdConfiguration(deviceId)

  const handleResetConfirm = async () => {
    await onResetToDefaults()
    setIsResetConfirmOpen(false)
  }

  if (isLoading) return <DarkEditDeviceSkeleton />

  const isThresholdInvalid = (value: number) =>
    !Number.isFinite(value) || value < MIN_THRESHOLD || value > MAX_THRESHOLD
  const hasInvalidThreshold = [
    accessGrantedThreshold,
    doorCloseThreshold,
    doorForcedBounceThreshold,
  ].some(isThresholdInvalid)
  const thresholdWarning = tVar(t, 'devices:crud_threshold_range_error', {
    min: MIN_THRESHOLD,
    max: MAX_THRESHOLD,
  })
  const thresholdInputProps = {
    min: MIN_THRESHOLD,
    max: MAX_THRESHOLD,
    inputMode: 'numeric' as const,
    type: 'number',
  }

  return (
    <>
      <DarkEditDeviceSection
        actions={
          <>
            <Button
              colorScheme={colorScheme}
              disabled={isResetDisabled}
              onClick={() => setIsResetConfirmOpen(true)}
              variant='secondary'
            >
              {t('common:reset')}
            </Button>
            <Button
              colorScheme={colorScheme}
              disabled={isSaveDisabled || hasInvalidThreshold}
              isLoading={isUpsertLoading}
              onClick={async () => {
                await onSave()
                onSaveCallback?.()
              }}
            >
              {isUpsertLoading ? t('common:saving') : t('common:save')}
            </Button>
          </>
        }
        colorScheme={colorScheme}
        description={tVar(t, 'devices:crud_threshold_description', {
          min: MIN_THRESHOLD,
          max: MAX_THRESHOLD,
        })}
        title={t('devices:crud_ai_thresholds_title')}
      >
        <Grid
          gap='16px'
          templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
        >
          <TextInput
            colorScheme={colorScheme}
            hasWarning={isThresholdInvalid(accessGrantedThreshold)}
            inputProps={thresholdInputProps}
            label={t('devices:crud_threshold_access_granted')}
            onValueChange={(value) =>
              handleAccessGrantedChange(value, Number(value))
            }
            value={String(accessGrantedThreshold)}
            warningText={thresholdWarning}
            width='100%'
          />
          <TextInput
            colorScheme={colorScheme}
            hasWarning={isThresholdInvalid(doorCloseThreshold)}
            inputProps={thresholdInputProps}
            label={t('devices:crud_threshold_door_closed')}
            onValueChange={(value) =>
              handleDoorCloseChange(value, Number(value))
            }
            value={String(doorCloseThreshold)}
            warningText={thresholdWarning}
            width='100%'
          />
          <TextInput
            colorScheme={colorScheme}
            hasWarning={isThresholdInvalid(doorForcedBounceThreshold)}
            inputProps={thresholdInputProps}
            label={t('devices:crud_threshold_door_bounce')}
            onValueChange={(value) =>
              handleDoorForcedBounceChange(value, Number(value))
            }
            value={String(doorForcedBounceThreshold)}
            warningText={thresholdWarning}
            width='100%'
          />
        </Grid>

        {isDefault && (
          <Text fontSize='12px' mt='16px' opacity={0.7}>
            {t('devices:crud_threshold_using_defaults')}
          </Text>
        )}
      </DarkEditDeviceSection>

      <Modal
        closeOnEsc={!isDeleteLoading}
        closeOnOverlayClick={!isDeleteLoading}
        colorScheme={colorScheme}
        isCloseHidden={isDeleteLoading}
        isOpen={isResetConfirmOpen}
        isSaveDestructive
        isSaveLoading={isDeleteLoading}
        onCancel={() => setIsResetConfirmOpen(false)}
        onClose={() => setIsResetConfirmOpen(false)}
        onSave={handleResetConfirm}
        saveButtonLabel={t('devices:crud_reset_threshold_title')}
        showCancelButton={!isDeleteLoading}
        size='compact'
        title={t('devices:crud_reset_threshold_title')}
      >
        <Box>{t('devices:crud_reset_threshold_confirm_full')}</Box>
      </Modal>
    </>
  )
}
